import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiService } from '../services/useApiService';
import { mapLoginError } from '../utils/authErrorMessage';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState(null);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const { auth, users, licenses } = useApiService();
  const navigate = useNavigate();
  const [sessionTimeoutDuration] = useState(30 * 60 * 1000); // 30 minutes default
  const [warningTime] = useState(5 * 60 * 1000); // 5 minutes before expiry
  const warningTimeoutRef = useRef();
  const sessionTimeoutRef = useRef();

  // License helper functions
  const hasFeature = useCallback((featureId) => {
    return user?.license?.features?.some(f => f === featureId) || false;
  }, [user?.license?.features]);

  const getLicenseFeatures = useCallback(() => {
    return user?.license?.features || [];
  }, [user?.license?.features]);

  const isTrialLicense = useCallback(() => {
    return user?.license?.isTrial || false;
  }, [user?.license?.isTrial]);

  const getLicenseStatus = useCallback(() => {
    return user?.license?.status;
  }, [user?.license?.status]);

  const refreshLicenseFeatures = useCallback(async () => {
    try {
      const response = await licenses.validate();
      const nextLicense = response?.data?.license;
      if (!nextLicense) return user?.license?.features || [];
      setUser((prev) => prev ? {
        ...prev,
        license: {
          ...prev.license,
          id: nextLicense.id,
          key: nextLicense.key,
          tenantId: nextLicense.tenantId,
          features: nextLicense.features || [],
          featureHistory: nextLicense.featureHistory || {},
          status: nextLicense.status,
          isTrial: nextLicense.isTrial,
          trialEndDate: nextLicense.trialEndDate,
          expiryDate: nextLicense.expiryDate
        }
      } : prev);
      return nextLicense.features || [];
    } catch (error) {
      return user?.license?.features || [];
    }
  }, [licenses, user?.license?.features]);

  const checkSession = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return false;

    try {
      const response = await auth.getCurrentUser();
      return !!response?.data;
    } catch (error) {
      if (error.response?.status === 401) {
        // Try to refresh token
        try {
          const refreshResponse = await auth.refresh();
          if (refreshResponse?.data?.accessToken) {
            localStorage.setItem('token', refreshResponse.data.accessToken);
            return true;
          }
        } catch (refreshError) {
          return false;
        }
      }
      return false;
    }
  }, [auth]);

  const handleSessionExpired = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login', {
      state: { message: 'Your session has expired. Please log in again.' }
    });
  }, [navigate]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        const isValid = await checkSession();
        if (isValid) {
          const response = await auth.getCurrentUser();
          if (response?.data) {
            setUser(response.data);
            setIsAuthenticated(true);
          }
        } else {
          handleSessionExpired();
        }
      } catch (error) {
        handleSessionExpired();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [auth, users, handleSessionExpired, checkSession]);

  useEffect(() => {
    let sessionCheckInterval;
    let warningTimeout;

    const checkSession = async () => {
      try {
        const isValid = await auth.checkSession();
        if (!isValid) {
          handleSessionExpired();
        }
      } catch (error) {
        // No console.log
      }
    };

    if (isAuthenticated) {
      sessionCheckInterval = setInterval(checkSession, 5 * 60 * 1000);

      if (sessionTimeout) {
        const timeUntilWarning = sessionTimeout - 5 * 60 * 1000;
        if (timeUntilWarning > 0) {
          warningTimeout = setTimeout(() => {
            setShowSessionWarning(true);
          }, timeUntilWarning);
        }
      }
    }

    return () => {
      clearInterval(sessionCheckInterval);
      clearTimeout(warningTimeout);
    };
  }, [auth, handleSessionExpired, isAuthenticated, sessionTimeout]);

  const login = async (email, password, productId) => {
    try {
      // Clear any existing session data
      localStorage.clear();
      sessionStorage.clear();
      
      const response = await auth.login({
        email,
        password,
        productId,
      });

      if (response?.data?.user && response?.data?.token) {
        const { user: userData, token, license } = response.data;

        // Store auth data in localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('userId', userData.id);
        localStorage.setItem('customerId', userData.customerId);
        localStorage.setItem('productId', userData.productId);
        localStorage.setItem('tenantId', userData.tenantId);

        // Set user with license information
        setUser({
          ...userData,
          license: {
            id: license.id,
            key: license.key,
            tenantId: license.tenantId,
            features: license.features || [],
            featureHistory: license.featureHistory || [],
            status: license.status,
            isTrial: license.isTrial,
            trialEndDate: license.trialEndDate,
            expiryDate: license.expiryDate
          }
        });
        setIsAuthenticated(true);
        
        const timeoutDuration = 8 * 60 * 60 * 1000; // 8 hours
        setSessionTimeout(Date.now() + timeoutDuration);

        // Navigate after successful login
        navigate('/dashboard');
        return { success: true };
      }

      return {
        success: false,
        error: 'Invalid response from server',
        code: 'INVALID_RESPONSE'
      };

    } catch (error) {
      return mapLoginError(error);
    }
  };

  const logout = async () => {
    try {
      await auth.logout();
    } catch (error) {
      // No console.log
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setSessionTimeout(null);
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login');
    }
  };

  const updateUser = async () => {
    try {
      const response = await auth.getCurrentUser();
      if (response?.data) {
        setUser(response.data);
        return response.data;
      }
    } catch (error) {
      // No console.log
    }
    return null;
  };

  const resetSessionTimers = useCallback(() => {
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);

    if (isAuthenticated) {
      // Set warning timeout
      warningTimeoutRef.current = setTimeout(() => {
        setShowSessionWarning(true);
      }, sessionTimeoutDuration - warningTime);

      // Set session timeout
      sessionTimeoutRef.current = setTimeout(() => {
        handleSessionExpired();
      }, sessionTimeoutDuration);
    }
  }, [isAuthenticated, sessionTimeoutDuration, warningTime]);

  // Add event listeners for user activity
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    const handleUserActivity = () => {
      if (isAuthenticated && !showSessionWarning) {
        resetSessionTimers();
      }
    };

    events.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [isAuthenticated, showSessionWarning, resetSessionTimers]);

  const refreshSession = useCallback(async () => {
    try {
      const response = await auth.refreshToken();
      if (response) {
        setShowSessionWarning(false);
        resetSessionTimers();
      }
    } catch (error) {
      handleSessionExpired();
    }
  }, [auth]);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);
    };
  }, []);

  // Initialize session timers on auth state change
  useEffect(() => {
    if (isAuthenticated) {
      resetSessionTimers();
    }
  }, [isAuthenticated, resetSessionTimers]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isAuthenticated,
        login,
        logout,
        loading,
        updateUser,
        showSessionWarning,
        setShowSessionWarning,
        checkSession,
        hasFeature,
        getLicenseFeatures,
        refreshLicenseFeatures,
        isTrialLicense,
        getLicenseStatus
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
